import React from 'react'

export type BadgeTone = 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info' | 'neutral'
export type BadgeVariant = 'solid' | 'subtle' | 'outline'
export type BadgeSize = 'sm' | 'md' | 'lg'

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  children: React.ReactNode
  tone?: BadgeTone
  variant?: BadgeVariant
  size?: BadgeSize
  dot?: boolean
  pulse?: boolean
  icon?: React.ReactNode
  className?: string
}

const toneClasses: Record<BadgeTone, Record<BadgeVariant, string>> = {
  primary: {
    solid:
      'bg-gradient-to-r from-amber-500 to-amber-600 text-amber-950 font-bold border-amber-600/30 shadow-sm dark:from-amber-400 dark:to-amber-500 dark:text-neutral-950 dark:border-amber-300/40',
    subtle:
      'bg-amber-500/15 text-amber-900 border-amber-500/30 backdrop-blur-[2px] dark:bg-amber-400/15 dark:text-amber-300 dark:border-amber-400/30',
    outline:
      'bg-transparent text-amber-900 border-amber-500/40 hover:bg-amber-500/10 dark:text-amber-300 dark:border-amber-400/45 dark:hover:bg-amber-400/10',
  },
  secondary: {
    solid:
      'bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold border-indigo-700/30 shadow-sm dark:from-indigo-500 dark:to-purple-500 dark:border-indigo-400/30',
    subtle:
      'bg-indigo-500/15 text-indigo-950 border-indigo-500/25 backdrop-blur-[2px] dark:bg-indigo-400/15 dark:text-indigo-200 dark:border-indigo-400/30',
    outline:
      'bg-transparent text-indigo-900 border-indigo-500/40 hover:bg-indigo-500/10 dark:text-indigo-300 dark:border-indigo-400/45 dark:hover:bg-indigo-400/10',
  },
  success: {
    solid:
      'bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold border-emerald-700/30 shadow-sm dark:from-emerald-500 dark:to-teal-500 dark:border-emerald-400/30',
    subtle:
      'bg-emerald-500/15 text-emerald-900 border-emerald-500/25 backdrop-blur-[2px] dark:bg-emerald-400/15 dark:text-emerald-300 dark:border-emerald-400/30',
    outline:
      'bg-transparent text-emerald-800 border-emerald-500/40 hover:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-400/45 dark:hover:bg-emerald-400/10',
  },
  warning: {
    solid:
      'bg-gradient-to-r from-amber-500 to-orange-500 text-amber-950 font-bold border-amber-600/30 shadow-sm dark:from-amber-400 dark:to-orange-400 dark:text-neutral-950 dark:border-amber-300/40',
    subtle:
      'bg-amber-500/15 text-amber-900 border-amber-500/30 backdrop-blur-[2px] dark:bg-amber-400/15 dark:text-amber-300 dark:border-amber-400/30',
    outline:
      'bg-transparent text-amber-900 border-amber-500/40 hover:bg-amber-500/10 dark:text-amber-300 dark:border-amber-400/45 dark:hover:bg-amber-400/10',
  },
  danger: {
    solid:
      'bg-gradient-to-r from-rose-600 to-red-600 text-white font-semibold border-rose-700/30 shadow-sm dark:from-rose-500 dark:to-red-500 dark:border-rose-400/30',
    subtle:
      'bg-rose-500/15 text-rose-900 border-rose-500/25 backdrop-blur-[2px] dark:bg-rose-400/15 dark:text-rose-300 dark:border-rose-400/30',
    outline:
      'bg-transparent text-rose-800 border-rose-500/40 hover:bg-rose-500/10 dark:text-rose-300 dark:border-rose-400/45 dark:hover:bg-rose-400/10',
  },
  info: {
    solid:
      'bg-gradient-to-r from-sky-600 to-blue-600 text-white font-semibold border-sky-700/30 shadow-sm dark:from-sky-500 dark:to-blue-500 dark:border-sky-400/30',
    subtle:
      'bg-sky-500/15 text-sky-950 border-sky-500/25 backdrop-blur-[2px] dark:bg-sky-400/15 dark:text-sky-300 dark:border-sky-400/30',
    outline:
      'bg-transparent text-sky-900 border-sky-500/40 hover:bg-sky-500/10 dark:text-sky-300 dark:border-sky-400/45 dark:hover:bg-sky-400/10',
  },
  neutral: {
    solid:
      'bg-slate-800 text-white font-semibold border-slate-900/30 shadow-sm dark:bg-slate-100 dark:text-slate-900 dark:border-white/20',
    subtle:
      'bg-slate-500/10 text-slate-700 border-slate-300/80 backdrop-blur-[2px] dark:bg-white/10 dark:text-slate-200 dark:border-white/15',
    outline:
      'bg-transparent text-slate-700 border-slate-300/90 hover:bg-slate-500/10 dark:text-slate-300 dark:border-slate-600/70 dark:hover:bg-white/10',
  },
}

const dotClasses: Record<BadgeTone, string> = {
  primary: 'bg-amber-500 dark:bg-amber-400',
  secondary: 'bg-indigo-500 dark:bg-indigo-400',
  success: 'bg-emerald-500 dark:bg-emerald-400',
  warning: 'bg-amber-500 dark:bg-amber-400',
  danger: 'bg-rose-500 dark:bg-rose-400',
  info: 'bg-sky-500 dark:bg-sky-400',
  neutral: 'bg-slate-400 dark:bg-slate-400',
}

const sizeClasses: Record<BadgeSize, string> = {
  sm: 'text-[11px] px-2 py-0.5 gap-1 leading-none',
  md: 'text-xs px-2.5 py-0.5 gap-1.5 leading-tight',
  lg: 'text-sm px-3 py-1 gap-2 leading-snug',
}

const baseClass =
  'inline-flex items-center justify-center font-semibold rounded-full border tracking-wide select-none transition-all duration-150 capitalize shrink-0'

const getDotBgClass = (safeVariant: BadgeVariant, safeTone: BadgeTone): string => {
  if (safeVariant !== 'solid') {
    return dotClasses[safeTone]
  }

  if (safeTone === 'primary' || safeTone === 'warning') {
    return 'bg-amber-950/80 dark:bg-neutral-900/80'
  }

  return 'bg-white/90'
}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      children,
      tone = 'neutral',
      variant = 'subtle',
      size = 'md',
      dot = false,
      pulse = false,
      icon,
      className = '',
      ...rest
    },
    ref,
  ) => {
    const safeTone = tone && toneClasses[tone] ? tone : 'neutral'
    const safeVariant = variant && toneClasses[safeTone]?.[variant] ? variant : 'subtle'
    const safeSize = size && sizeClasses[size] ? size : 'md'
    const showDot = dot || pulse
    const dotBg = getDotBgClass(safeVariant, safeTone)

    return (
      <span
        ref={ref}
        className={`${baseClass} ${sizeClasses[safeSize]} ${toneClasses[safeTone][safeVariant]} ${className}`}
        {...rest}
      >
        {showDot && (
          <span className="relative flex h-1.5 w-1.5 shrink-0 items-center justify-center" aria-hidden="true">
            {pulse && (
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${dotBg}`} />
            )}
            <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${dotBg}`} />
          </span>
        )}
        {icon && (
          <span className="inline-flex shrink-0 items-center justify-center text-current opacity-85" aria-hidden="true">
            {icon}
          </span>
        )}
        {children}
      </span>
    )
  },
)

Badge.displayName = 'Badge'
